import React, { useState, useEffect } from 'react';
import { 
  Eye, Shield, AlertTriangle, CheckCircle, Clock, 
  Plus, Trash2, Search, Mail, Lock, RefreshCw, X,
  AlertCircle, Database, Key, ExternalLink, Bell
} from 'lucide-react';
import { darkwebAPI } from '../lib/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e'
};

const DarkWebDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [monitoredEmails, setMonitoredEmails] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [showPasswordCheck, setShowPasswordCheck] = useState(false);
  const [showBreachDetail, setShowBreachDetail] = useState(false);
  const [selectedBreach, setSelectedBreach] = useState(null);
  
  // Form states
  const [emailForm, setEmailForm] = useState({ email: '', employeeName: '', department: '' });
  const [passwordToCheck, setPasswordToCheck] = useState('');
  const [passwordResult, setPasswordResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, emailsRes, breachesRes] = await Promise.all([
        darkwebAPI.getStats(),
        darkwebAPI.getMonitored(),
        darkwebAPI.getBreaches()
      ]);
      setStats(statsRes.data);
      setMonitoredEmails(emailsRes.data);
      setBreaches(breachesRes.data);
    } catch (error) {
      console.error('Error loading dark web data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    try {
      await darkwebAPI.addMonitored(emailForm);
      setShowAddEmail(false);
      setEmailForm({ email: '', employeeName: '', department: '' });
      loadData();
    } catch (error) {
      console.error('Error adding email:', error);
    }
  };

  const handleRemoveEmail = async (emailId) => {
    if (!window.confirm('Remove this email from monitoring?')) return;
    try {
      await darkwebAPI.removeMonitored(emailId);
      loadData();
    } catch (error) {
      console.error('Error removing email:', error);
    }
  };

  const handleCheckEmail = async (emailId) => {
    setCheckingEmail(emailId);
    try {
      const res = await darkwebAPI.checkEmail(emailId);
      if (res.data.status === 'rate_limited') {
        alert('Rate limited by breach database. Please wait 2 seconds between checks.');
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setCheckingEmail(null);
    }
  };

  const handleCheckPassword = async () => {
    if (!passwordToCheck) return;
    try {
      const res = await darkwebAPI.checkPassword({ password: passwordToCheck });
      setPasswordResult(res.data);
    } catch (error) {
      console.error('Error checking password:', error);
    }
  };

  const handleUpdateBreachStatus = async (breachId, status) => {
    try {
      await darkwebAPI.updateBreachStatus(breachId, { status });
      loadData();
    } catch (error) {
      console.error('Error updating breach status:', error);
    }
  };

  const filteredEmails = monitoredEmails.filter(e =>
    e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.employeeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRiskBadgeClass = (level) => {
    const classes = {
      low: 'bg-green-500/20 text-green-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      high: 'bg-orange-500/20 text-orange-400',
      critical: 'bg-red-500/20 text-red-400',
      unknown: 'bg-slate-500/20 text-slate-400'
    };
    return classes[level] || classes.unknown;
  };

  const getSeverityBadgeClass = (severity) => getRiskBadgeClass(severity);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const severityDistributionData = stats ? [
    { name: 'Critical', value: stats.severityDistribution?.critical || 0, color: SEVERITY_COLORS.critical },
    { name: 'High', value: stats.severityDistribution?.high || 0, color: SEVERITY_COLORS.high },
    { name: 'Medium', value: stats.severityDistribution?.medium || 0, color: SEVERITY_COLORS.medium },
    { name: 'Low', value: stats.severityDistribution?.low || 0, color: SEVERITY_COLORS.low }
  ] : [];

  return (
    <div data-testid="darkweb-dashboard" className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              Dark Web Monitoring
            </h1>
            <p className="text-slate-400 mt-2">Monitor email addresses for data breaches and exposed credentials</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowPasswordCheck(true)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              data-testid="check-password-btn"
            >
              <Key className="w-4 h-4" />
              Check Password
            </button>
            <button
              onClick={() => setShowAddEmail(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              data-testid="add-email-btn"
            >
              <Plus className="w-4 h-4" />
              Monitor Email
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Monitored</p>
                  <p className="text-2xl font-bold text-white">{stats.totalMonitored}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Total Breaches</p>
                  <p className="text-2xl font-bold text-white">{stats.totalBreaches}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">New Breaches</p>
                  <p className="text-2xl font-bold text-white">{stats.newBreaches}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Password Breaches</p>
                  <p className="text-2xl font-bold text-white">{stats.passwordBreaches}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">High Risk Emails</p>
                  <p className="text-2xl font-bold text-white">{stats.highRiskEmails}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
          {['overview', 'monitored', 'breaches'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-purple-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Severity Distribution */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Breach Severity Distribution</h3>
              {severityDistributionData.every(d => d.value === 0) ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                    <p className="text-slate-400">No breaches detected</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={severityDistributionData.filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {severityDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-4 mt-4 flex-wrap">
                    {severityDistributionData.map((item) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="text-slate-400 text-sm">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Recent Breaches */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Recent Breaches
              </h3>
              {breaches.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 mx-auto text-green-500 mb-3" />
                  <p className="text-slate-400">No breaches found</p>
                  <p className="text-slate-500 text-sm">Your monitored emails are safe</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {breaches.slice(0, 10).map((breach) => (
                    <div 
                      key={breach.id} 
                      className="p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                      onClick={() => {
                        setSelectedBreach(breach);
                        setShowBreachDetail(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-medium">{breach.breachTitle || breach.breachName}</h4>
                          <p className="text-slate-400 text-sm">{breach.email}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${getSeverityBadgeClass(breach.severity)}`}>
                          {breach.severity}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                        <span>Breach Date: {breach.breachDate || 'Unknown'}</span>
                        <span className={`px-2 py-0.5 rounded ${
                          breach.status === 'new' ? 'bg-red-500/20 text-red-400' :
                          breach.status === 'acknowledged' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {breach.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* High Risk Emails */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 lg:col-span-2">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                High Risk Email Accounts
              </h3>
              {monitoredEmails.filter(e => e.riskLevel === 'high' || e.riskLevel === 'critical').length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                  <p className="text-slate-400">No high-risk email accounts</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {monitoredEmails.filter(e => e.riskLevel === 'high' || e.riskLevel === 'critical').map((email) => (
                    <div key={email.id} className={`p-4 rounded-lg border ${
                      email.riskLevel === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-orange-500/10 border-orange-500/30'
                    }`}>
                      <div className="flex items-center justify-between">
                        <Mail className={`w-5 h-5 ${email.riskLevel === 'critical' ? 'text-red-400' : 'text-orange-400'}`} />
                        <span className={`px-2 py-1 rounded text-xs ${getRiskBadgeClass(email.riskLevel)}`}>
                          {email.riskLevel}
                        </span>
                      </div>
                      <h4 className="text-white font-medium mt-2 truncate">{email.email}</h4>
                      <p className="text-slate-400 text-sm">{email.employeeName || 'Unknown'}</p>
                      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span>{email.breachCount} breaches</span>
                        <button
                          onClick={() => handleCheckEmail(email.id)}
                          disabled={checkingEmail === email.id}
                          className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
                        >
                          {checkingEmail === email.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3 h-3" />
                          )}
                          Check
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Monitored Tab */}
        {activeTab === 'monitored' && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Monitored Email Addresses</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search emails..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            {filteredEmails.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No emails being monitored</p>
                <button
                  onClick={() => setShowAddEmail(true)}
                  className="mt-3 text-purple-400 hover:text-purple-300"
                >
                  Add your first email to monitor
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Employee</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Department</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Risk Level</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Breaches</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Last Checked</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmails.map((email) => (
                      <tr key={email.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                        <td className="p-4">
                          <p className="text-white font-medium">{email.email}</p>
                        </td>
                        <td className="p-4 text-slate-300">{email.employeeName || '-'}</td>
                        <td className="p-4 text-slate-300">{email.department || '-'}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getRiskBadgeClass(email.riskLevel)}`}>
                            {email.riskLevel}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`font-bold ${email.breachCount > 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {email.breachCount}
                          </span>
                        </td>
                        <td className="p-4 text-slate-400 text-sm">
                          {email.lastChecked ? new Date(email.lastChecked).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCheckEmail(email.id)}
                              disabled={checkingEmail === email.id}
                              className="p-2 text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                              title="Check for breaches"
                            >
                              {checkingEmail === email.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleRemoveEmail(email.id)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Remove from monitoring"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Breaches Tab */}
        {activeTab === 'breaches' && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">Breach Records</h3>
            </div>
            {breaches.length === 0 ? (
              <div className="p-12 text-center">
                <Shield className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-slate-400">No breaches found</p>
                <p className="text-slate-500 text-sm">Your monitored emails are safe</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="text-left p-4 text-slate-400 font-medium">Breach</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Severity</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Data Exposed</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Breach Date</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breaches.map((breach) => (
                      <tr key={breach.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                        <td className="p-4">
                          <p className="text-white font-medium">{breach.breachTitle || breach.breachName}</p>
                          <p className="text-slate-500 text-xs">{breach.breachDomain}</p>
                        </td>
                        <td className="p-4 text-slate-300">{breach.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getSeverityBadgeClass(breach.severity)}`}>
                            {breach.severity}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {breach.dataClasses?.slice(0, 3).map((dc, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">
                                {dc}
                              </span>
                            ))}
                            {breach.dataClasses?.length > 3 && (
                              <span className="text-slate-500 text-xs">+{breach.dataClasses.length - 3} more</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-slate-400 text-sm">{breach.breachDate || 'Unknown'}</td>
                        <td className="p-4">
                          <select
                            value={breach.status}
                            onChange={(e) => handleUpdateBreachStatus(breach.id, e.target.value)}
                            className={`px-2 py-1 rounded text-xs bg-transparent border ${
                              breach.status === 'new' ? 'border-red-500/50 text-red-400' :
                              breach.status === 'acknowledged' ? 'border-yellow-500/50 text-yellow-400' :
                              breach.status === 'remediated' ? 'border-green-500/50 text-green-400' :
                              'border-slate-500/50 text-slate-400'
                            }`}
                          >
                            <option value="new">New</option>
                            <option value="acknowledged">Acknowledged</option>
                            <option value="remediated">Remediated</option>
                            <option value="ignored">Ignored</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => {
                              setSelectedBreach(breach);
                              setShowBreachDetail(true);
                            }}
                            className="p-2 text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Email Modal */}
      {showAddEmail && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Monitor Email Address</h3>
              <button onClick={() => setShowAddEmail(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddEmail} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-300 text-sm mb-1">Email Address *</label>
                <input
                  type="email"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Employee Name</label>
                <input
                  type="text"
                  value={emailForm.employeeName}
                  onChange={(e) => setEmailForm({...emailForm, employeeName: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Department</label>
                <input
                  type="text"
                  value={emailForm.department}
                  onChange={(e) => setEmailForm({...emailForm, department: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddEmail(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                >
                  Add to Monitoring
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Check Modal */}
      {showPasswordCheck && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Password Breach Check</h3>
              <button onClick={() => {
                setShowPasswordCheck(false);
                setPasswordToCheck('');
                setPasswordResult(null);
              }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-slate-300 text-sm mb-1">Enter Password to Check</label>
                <input
                  type="password"
                  value={passwordToCheck}
                  onChange={(e) => setPasswordToCheck(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter password..."
                />
                <p className="text-slate-500 text-xs mt-1">
                  Your password is checked securely using k-anonymity and is never sent in full
                </p>
              </div>
              
              {passwordResult && (
                <div className={`p-4 rounded-lg ${passwordResult.isPwned ? 'bg-red-500/20 border border-red-500/30' : 'bg-green-500/20 border border-green-500/30'}`}>
                  <div className="flex items-center gap-2">
                    {passwordResult.isPwned ? (
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    <span className={passwordResult.isPwned ? 'text-red-400' : 'text-green-400'}>
                      {passwordResult.message}
                    </span>
                  </div>
                  {passwordResult.isPwned && (
                    <p className="text-slate-400 text-sm mt-2">
                      This password should be changed immediately across all accounts where it's used.
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowPasswordCheck(false);
                    setPasswordToCheck('');
                    setPasswordResult(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Close
                </button>
                <button
                  onClick={handleCheckPassword}
                  disabled={!passwordToCheck}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Check Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breach Detail Modal */}
      {showBreachDetail && selectedBreach && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
              <div>
                <h3 className="text-xl font-semibold text-white">{selectedBreach.breachTitle || selectedBreach.breachName}</h3>
                <p className="text-slate-400 text-sm">{selectedBreach.breachDomain}</p>
              </div>
              <button onClick={() => setShowBreachDetail(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Severity & Status */}
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-lg ${getSeverityBadgeClass(selectedBreach.severity)}`}>
                  {selectedBreach.severity} severity
                </span>
                <span className={`px-3 py-1 rounded-lg ${
                  selectedBreach.status === 'new' ? 'bg-red-500/20 text-red-400' :
                  selectedBreach.status === 'acknowledged' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {selectedBreach.status}
                </span>
              </div>

              {/* Affected Email */}
              <div>
                <h4 className="text-white font-medium mb-2">Affected Email</h4>
                <p className="text-slate-300">{selectedBreach.email}</p>
              </div>

              {/* Description */}
              {selectedBreach.description && (
                <div>
                  <h4 className="text-white font-medium mb-2">Description</h4>
                  <p className="text-slate-400 text-sm" dangerouslySetInnerHTML={{ __html: selectedBreach.description }}></p>
                </div>
              )}

              {/* Data Exposed */}
              {selectedBreach.dataClasses?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Data Exposed</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedBreach.dataClasses.map((dc, idx) => (
                      <span 
                        key={idx} 
                        className={`px-3 py-1 rounded-lg text-sm ${
                          dc.toLowerCase().includes('password') ? 'bg-red-500/20 text-red-400' :
                          dc.toLowerCase().includes('email') ? 'bg-orange-500/20 text-orange-400' :
                          'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {dc}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Breach Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Breach Date</p>
                  <p className="text-white">{selectedBreach.breachDate || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Added to Database</p>
                  <p className="text-white">{selectedBreach.addedDate || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Records Breached</p>
                  <p className="text-white">{selectedBreach.pwnCount?.toLocaleString() || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Verified</p>
                  <p className={selectedBreach.isVerified ? 'text-green-400' : 'text-slate-400'}>
                    {selectedBreach.isVerified ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    handleUpdateBreachStatus(selectedBreach.id, 'acknowledged');
                    setShowBreachDetail(false);
                  }}
                  className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg"
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => {
                    handleUpdateBreachStatus(selectedBreach.id, 'remediated');
                    setShowBreachDetail(false);
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  Mark Remediated
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DarkWebDashboard;
